import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Lock as LockIcon,
  Warning as WarningIcon,
  Upgrade as UpgradeIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { useAuth } from '../context/AuthContext';
import SubscriptionPromptModal from './SubscriptionPromptModal';

const SubscriptionGate = ({ 
  children, 
  featureName = 'this feature',
  showPreview = false,
  previewMessage = 'This feature requires an active subscription'
}) => {
  const {
    loading,
    needsSubscription,
    hasActiveSubscription,
    isOnTrial,
    isTrialExpired,
    isSubscriptionCancelled,
    getTrialDaysRemaining,
    getStatusMessage,
    subscriptionData
  } = useSubscriptionGuard();

  const { isSubscriptionModalActive, setGlobalSubscriptionModal, triggerSubscriptionModal } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Checking subscription status...
        </Typography>
      </Box>
    );
  }

  // Allow access if user has active subscription or active trial
  if (hasActiveSubscription() || (isOnTrial() && !isTrialExpired())) {
    return children; // No modal needed when access is allowed
  }

  // Function to open subscription modal with current user status
  const openSubscriptionModal = () => {
    if (!isSubscriptionModalActive) {
      const currentStatus = {
        status: isTrialExpired() ? 'expired' : 
                isSubscriptionCancelled() ? 'cancelled' : 'none',
        days_remaining: getTrialDaysRemaining(),
        is_cancelled_but_active: isSubscriptionCancelled() && !needsSubscription()
      };
      triggerSubscriptionModal(currentStatus, 'SubscriptionGate');
    }
  };

  // Block access for expired trials, cancelled subscriptions, or no subscription
  const getBlockedContent = () => {
    let icon = <LockIcon sx={{ fontSize: 48, color: 'text.secondary' }} />;
    let title = 'Subscription Required';
    let message = previewMessage;
    let severity = 'info';

    if (isTrialExpired()) {
      icon = <AccessTimeIcon sx={{ fontSize: 48, color: 'warning.main' }} />;
      title = 'Trial Expired';
      message = `Your trial has expired. Subscribe now to continue using ${featureName}.`;
      severity = 'warning';
    } else if (isSubscriptionCancelled()) {
      icon = <WarningIcon sx={{ fontSize: 48, color: 'error.main' }} />;
      title = 'Subscription Cancelled';
      message = `Your subscription has been cancelled. Reactivate your subscription to continue using ${featureName}.`;
      severity = 'error';
    }

    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          backgroundColor: severity === 'error' ? 'error.light' : 
                          severity === 'warning' ? 'warning.light' : 'grey.50',
          border: `1px solid ${severity === 'error' ? 'error.main' : 
                                severity === 'warning' ? 'warning.main' : 'grey.300'}`
        }}
      >
        {icon}
        
        <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
          <Chip 
            label={getStatusMessage()} 
            color={isTrialExpired() ? 'warning' : isSubscriptionCancelled() ? 'error' : 'default'}
            variant="outlined"
          />
        </Stack>

        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<UpgradeIcon />}
          onClick={openSubscriptionModal}
          disabled={isSubscriptionModalActive} // Disable if modal is already active
          sx={{ 
            minWidth: 200,
            py: 1.5,
            fontWeight: 600
          }}
        >
          {isSubscriptionCancelled() ? 'Reactivate Subscription' : 'Upgrade Now'}
        </Button>

        {showPreview && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Preview Mode - Limited functionality available
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  // Show modal only if it's controlled by this component (not by AuthContext)
  const shouldShowLocalModal = false; // Remove local modal entirely - let AuthContext handle all modals

  return (
    <>
      {showPreview ? (
        <Box sx={{ position: 'relative' }}>
          {/* Overlay to block interaction */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'not-allowed'
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openSubscriptionModal();
            }}
          >
            <Box textAlign="center">
              <LockIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Click to upgrade
              </Typography>
            </Box>
          </Box>
          
          {/* Disabled preview content */}
          <Box sx={{ filter: 'blur(2px)', pointerEvents: 'none' }}>
            {children}
          </Box>
        </Box>
      ) : (
        getBlockedContent()
      )}
    </>
  );
};

export default SubscriptionGate; 